
import React, { useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { SimulationLog } from './types';
import { Entity } from './types';
import { motion } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface ExecutionLogsProps {
  logs: SimulationLog[];
  entities: Entity[];
  clearLogs: () => void;
}

export const ExecutionLogs = ({ logs, entities, clearLogs }: ExecutionLogsProps) => {
  const logContainerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Auto-scroll to bottom on new logs
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  const getEntityName = (entityId?: string) => {
    if (!entityId) return null;
    const entity = entities.find(e => e.id === entityId);
    return entity ? entity.name : entityId;
  };

  const getLogIcon = (type: string) => {
    switch (type) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'info':
      default:
        return 'ℹ️';
    }
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <Card className="flex-1 min-h-0 flex flex-col">
      <CardHeader className="py-3 px-4 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Execution Logs</CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={clearLogs}
          className="h-8 px-2"
          disabled={logs.length === 0}
        >
          <Trash2 className="h-4 w-4 mr-1" />
          <span className="text-xs">Clear</span>
        </Button>
      </CardHeader>
      <CardContent className="p-0 overflow-hidden flex-1 flex flex-col">
        <div
          ref={logContainerRef}
          className="flex-1 overflow-y-auto px-4 py-2 space-y-1 font-mono text-xs bg-muted/30 dark:bg-slate-900/30 rounded-b-lg"
        >
          {logs.length === 0 ? (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              <p>No logs yet. Start the simulation to see execution logs.</p>
            </div>
          ) : (
            logs.map((log, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={cn(
                  "py-1 px-2 rounded-sm flex items-start",
                  log.type === 'error' ? "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300" :
                  log.type === 'warning' ? "bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300" :
                  log.type === 'success' ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300" :
                  "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
                )}
              >
                <div className="mr-2 flex-shrink-0">{getLogIcon(log.type)}</div>
                <div className="flex-1 min-w-0">
                  <div className={cn(
                    "flex gap-1",
                    isMobile && "flex-col"
                  )}>
                    <span className="font-semibold flex-shrink-0">
                      {formatTimestamp(log.timestamp)}
                    </span>
                    {log.entityId && (
                      <span className="text-muted-foreground">
                        [{getEntityName(log.entityId)}]
                      </span>
                    )}
                  </div>
                  <div className="break-words">{log.message}</div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};
